import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'devextreme-react/button';
import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import {
  GridDetailPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridToolbarItems,
  OpsSiteGridChrome,
  opsGridDetailPopupSizeKey
} from '../opsDataGrid';
import PieChart, { Legend, Series, Label as ChartLabel } from 'devextreme-react/pie-chart';
import Popup from 'devextreme-react/popup';
import ScrollView from 'devextreme-react/scroll-view';
import SelectBox from 'devextreme-react/select-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { showDeleteConfirmDialog } from '../dialogs/confirmDialog';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import {
  SiteSupportAdminRowActions,
  siteSupportAdminActionsColumnWidth
} from './SiteSupportAdminRowActions';
import { FeedbackDiscussionChat } from './FeedbackDiscussionChat';
import { useFeedbackDiscussion } from './useFeedbackDiscussion';
import type { BugDetail, BugListItem, BugMetrics, SiteFeedbackApi } from './types';
import { isImageAttachmentContentType, readBuildMetadata } from './types';

const FALLBACK_BUG_STATUSES = ['Open', 'In-Progress', 'Deployed', 'Not a Bug'];
const FALLBACK_WORKLOADS = ['XS', 'S', 'M', 'L', 'XL'];
const BUGS_GRID_STATE_KEY = 'site-support-bugs-grid';

function readCurrentSiteVersion(): string {
  const meta = readBuildMetadata();
  return meta.version?.trim() || meta.productVersion?.trim() || meta.web?.buildVersion?.trim() || '';
}

export interface AdminBugsPageProps {
  api: SiteFeedbackApi;
  /** When true, admin-only actions (edit status/fix, delete) are available. */
  canManage?: boolean;
  currentUserId?: string;
  currentUserName?: string;
}

export function AdminBugsPage({
  api,
  canManage = false,
  currentUserId = 'admin',
  currentUserName
}: AdminBugsPageProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [loading, setLoading] = useState(false);
  const [bugs, setBugs] = useState<BugListItem[]>([]);
  const [metrics, setMetrics] = useState<BugMetrics | null>(null);
  const [detail, setDetail] = useState<BugDetail | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('In-Progress');
  const [editVersion, setEditVersion] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTicket, setEditTicket] = useState('');
  const [editWorkload, setEditWorkload] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [statuses, setStatuses] = useState<string[]>(FALLBACK_BUG_STATUSES);
  const [workloads, setWorkloads] = useState<string[]>(FALLBACK_WORKLOADS);
  const [lightbox, setLightbox] = useState<{
    attachmentId: string;
    fileName: string;
  } | null>(null);
  const currentSiteVersion = useMemo(() => readCurrentSiteVersion(), []);
  const discussion = useFeedbackDiscussion(api, 'bug', detail?.bugId ?? null, viewOpen && Boolean(detail));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([api.listBugs(0, 500), api.getBugMetrics()]);
      setBugs(list.items);
      setMetrics(m);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load bugs.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void Promise.all([api.listActiveMeta('BugStatus'), api.listActiveMeta('Workload')])
      .then(([statusRows, workloadRows]) => {
        if (statusRows.length) setStatuses(statusRows.map((r) => r.name));
        if (workloadRows.length) setWorkloads(workloadRows.map((r) => r.name));
      })
      .catch(() => undefined);
  }, [api]);

  const severityData = useMemo(
    () => metrics?.bySeverity.map((s) => ({ label: s.label, value: s.count })) ?? [],
    [metrics]
  );
  const statusData = useMemo(
    () => metrics?.byStatus.map((s) => ({ label: s.label, value: s.count })) ?? [],
    [metrics]
  );

  const openView = useCallback(
    async (bugId: string) => {
      try {
        setDetail(await api.getBug(bugId));
        setViewOpen(true);
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to load bug.');
      }
    },
    [api]
  );

  const openEdit = useCallback(async (bugId: string) => {
    if (!canManage) return;

    try {
      const b = await api.getBug(bugId);
      setDetail(b);
      setEditStatus(b.status === 'Open' ? (statuses.includes('In-Progress') ? 'In-Progress' : b.status) : b.status);
      setEditVersion(b.versionFixed ?? '');
      setEditNotes(b.resolutionNotes ?? '');
      setEditTicket(b.ticketUrl ?? '');
      setEditWorkload(b.workload ?? '');
      setEditStart(b.startAt ? b.startAt.slice(0, 10) : '');
      setEditEnd(b.endAt ? b.endAt.slice(0, 10) : '');
      setEditOpen(true);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load bug.');
    }
  }, [api, canManage, statuses]);

  const saveEdit = useCallback(async () => {
    if (!detail || !canManage) return;
    try {
      await api.updateBug(detail.bugId, {
        status: editStatus,
        versionFixed: editVersion || undefined,
        resolutionNotes: editNotes || undefined,
        ticketUrl: editTicket || undefined,
        workload: editWorkload || null,
        startAt: editStart ? new Date(`${editStart}T00:00:00`).toISOString() : null,
        endAt: editEnd ? new Date(`${editEnd}T23:59:59`).toISOString() : null
      });
      showSuccessToast('Bug updated.');
      setEditOpen(false);
      void load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Update failed.');
    }
  }, [
    api,
    canManage,
    detail,
    editEnd,
    editNotes,
    editStart,
    editStatus,
    editTicket,
    editVersion,
    editWorkload,
    load
  ]);

  const claimBug = useCallback(async () => {
    if (!detail || !canManage) return;
    try {
      await api.claimBug(detail.bugId, Boolean(detail.assigneeUserId));
      showSuccessToast(detail.assigneeUserId ? 'Reclaimed.' : 'Claimed.');
      setDetail(await api.getBug(detail.bugId));
      void load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Claim failed.');
    }
  }, [api, canManage, detail, load]);

  const deleteBug = useCallback(
    async (bugId: string) => {
      if (!canManage) return;
      if (!(await showDeleteConfirmDialog(`Delete bug ${bugId}? This cannot be undone.`))) return;

      try {
        await api.deleteBug(bugId);
        showSuccessToast('Bug deleted.');
        if (detail?.bugId === bugId) {
          setViewOpen(false);
          setEditOpen(false);
          setDetail(null);
        }
        void load();
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to delete bug.');
      }
    },
    [api, canManage, detail?.bugId, load]
  );

  const applyCurrentVersion = useCallback(() => {
    if (currentSiteVersion) setEditVersion(currentSiteVersion);
  }, [currentSiteVersion]);

  const renderViewContent = useCallback(() => {
    if (!detail) {
      return <div className="site-support-popup-body">Loading bug details…</div>;
    }

    return (
      <TabPanel
        className="ops-grid-detail-tab-panel site-support-ticket-tabs"
        deferRendering={false}
        animationEnabled={false}
        swipeEnabled={false}
        showNavButtons={false}
      >
        <TabItem title="Details">
          <div className="site-support-popup-body site-support-detail">
            <dl>
          <dt>Bug ID</dt>
          <dd>{detail.bugId}</dd>
          <dt>Subject</dt>
          <dd>{detail.subject}</dd>
          <dt>Severity</dt>
          <dd>{detail.severity}</dd>
          <dt>Status</dt>
          <dd>{detail.status}</dd>
          <dt>Owner</dt>
          <dd>{detail.assigneeDisplayName ?? detail.assigneeUserId ?? 'Unclaimed'}</dd>
          <dt>Workload</dt>
          <dd>{detail.workload ?? '—'}</dd>
          <dt>Start / End</dt>
          <dd>
            {detail.startAt ? new Date(detail.startAt).toLocaleDateString() : '—'}
            {' → '}
            {detail.endAt ? new Date(detail.endAt).toLocaleDateString() : '—'}
          </dd>
          <dt>Ticket URL</dt>
          <dd>{detail.ticketUrl ?? '—'}</dd>
          <dt>Version fixed</dt>
          <dd>{detail.versionFixed ?? '—'}</dd>
          <dt>Submitted</dt>
          <dd>{new Date(detail.submittedAt).toLocaleString()}</dd>
          <dt>Submitted by</dt>
          <dd>{detail.submittedByDisplayName ?? detail.submittedBy}</dd>
          <dt>Watchers</dt>
          <dd>
            {detail.watchers?.length
              ? detail.watchers.map((w) => w.displayName || w.userId).join(', ')
              : '—'}
          </dd>
          <dt>Page URL</dt>
          <dd>{detail.pageUrl ?? '—'}</dd>
            </dl>
            <div className="site-support-detail__message">{detail.message}</div>
            {detail.attachments.length > 0 ? (
              <ul className="site-support-attachments">
                {detail.attachments.map((a) => {
                  const isImage = isImageAttachmentContentType(a.contentType);
                  if (!isImage) {
                    return (
                      <li key={a.attachmentId}>
                        <a href={api.attachmentUrl(a.attachmentId, { download: true })} rel="noreferrer">
                          {a.fileName}
                        </a>
                      </li>
                    );
                  }
                  const inlineUrl = api.attachmentUrl(a.attachmentId, { inline: true });
                  return (
                    <li key={a.attachmentId} className="site-support-attachments__image-item">
                      <button
                        type="button"
                        className="site-support-attachments__thumb-btn"
                        aria-label={`Open ${a.fileName}`}
                        onClick={() => setLightbox({ attachmentId: a.attachmentId, fileName: a.fileName })}
                      >
                        <img
                          className="site-support-attachments__thumb"
                          src={inlineUrl}
                          alt={a.fileName}
                          loading="lazy"
                        />
                        <span className="site-support-attachments__hover-preview" aria-hidden="true">
                          <img src={inlineUrl} alt="" />
                        </span>
                        <span className="site-support-attachments__thumb-name">{a.fileName}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </TabItem>
        <TabItem title="Discussion">
          <FeedbackDiscussionChat
            title=""
            messages={discussion.messages}
            currentUserId={currentUserId}
            currentUserName={currentUserName ?? currentUserId}
            loading={discussion.loading}
            onMessageEntered={discussion.post}
            onMessageDeleted={discussion.remove}
          />
        </TabItem>
      </TabPanel>
    );
  }, [api, currentUserId, currentUserName, detail, discussion]);

  const renderEditContent = useCallback(
    () => (
      <div className="site-support-popup-body site-support-form">
        <label className="site-support-label">Status</label>
        <SelectBox items={statuses} value={editStatus} onValueChanged={(e) => setEditStatus(String(e.value ?? ''))} />
        <label className="site-support-label">Workload</label>
        <SelectBox
          items={['', ...workloads]}
          value={editWorkload}
          onValueChanged={(e) => setEditWorkload(String(e.value ?? ''))}
        />
        <label className="site-support-label">Start date</label>
        <TextBox value={editStart} onValueChanged={(e) => setEditStart(String(e.value ?? ''))} placeholder="yyyy-MM-dd" />
        <label className="site-support-label">End date</label>
        <TextBox value={editEnd} onValueChanged={(e) => setEditEnd(String(e.value ?? ''))} placeholder="yyyy-MM-dd" />
        <label className="site-support-label">Version fixed</label>
        <div className="site-support-version-row">
          <TextBox value={editVersion} onValueChanged={(e) => setEditVersion(String(e.value ?? ''))} />
          {currentSiteVersion ? (
            <Button
              text={`Use ${currentSiteVersion}`}
              stylingMode="outlined"
              hint="Set version fixed to the current site build"
              onClick={applyCurrentVersion}
            />
          ) : null}
        </div>
        <label className="site-support-label">Resolution notes (optional)</label>
        <TextArea value={editNotes} height={100} onValueChanged={(e) => setEditNotes(String(e.value ?? ''))} />
        <label className="site-support-label">Ticket URL (optional)</label>
        <TextBox value={editTicket} onValueChanged={(e) => setEditTicket(String(e.value ?? ''))} placeholder="https://..." />
        <div className="site-support-form-actions">
          <Button text="Cancel" stylingMode="outlined" onClick={() => setEditOpen(false)} />
          <Button text="Update" type="default" onClick={() => void saveEdit()} />
        </div>
      </div>
    ),
    [
      applyCurrentVersion,
      currentSiteVersion,
      editEnd,
      editNotes,
      editStart,
      editStatus,
      editTicket,
      editVersion,
      editWorkload,
      saveEdit,
      statuses,
      workloads
    ]
  );

  return (
    <>
      <ScrollView>
        <div className="ops-page site-support-admin">
          <h2 className="dashboard-title">Bugs</h2>

          <div className="site-support-charts">
            <div className="site-support-chart-card">
              <div className="site-support-chart-card__title">Bugs by severity</div>
              <PieChart dataSource={severityData} type="doughnut">
                <Series argumentField="label" valueField="value">
                  <ChartLabel visible />
                </Series>
                <Legend visible />
              </PieChart>
            </div>
            <div className="site-support-chart-card">
              <div className="site-support-chart-card__title">Bugs by status</div>
              <PieChart dataSource={statusData} type="doughnut">
                <Series argumentField="label" valueField="value">
                  <ChartLabel visible />
                </Series>
                <Legend visible />
              </PieChart>
            </div>
          </div>

          <h3 className="site-support-section-title">Bug reports</h3>
          <DataGrid
            ref={gridRef}
            dataSource={bugs}
            keyExpr="bugId"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
          >
            <OpsSiteGridChrome
              stateStorageKey={BUGS_GRID_STATE_KEY}
              searchPlaceholder="Search bugs…"
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={gridRef}
                loading={loading}
                onRefresh={() => void load()}
                refreshHint="Refresh bug reports"
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Column dataField="bugId" caption="Bug ID" width={140} />
            <Column dataField="subject" caption="Subject" />
            <Column dataField="severity" caption="Severity" width={100} />
            <Column dataField="status" caption="Status" width={120} />
            <Column dataField="assigneeDisplayName" caption="Owner" width={140} />
            <Column dataField="workload" caption="Workload" width={90} />
            <Column dataField="versionFixed" caption="Version fixed" width={110} />
            <Column dataField="submittedAt" caption="Submitted" dataType="datetime" width={160} />
            <Column
              caption=""
              width={siteSupportAdminActionsColumnWidth(canManage ? 3 : 1)}
              minWidth={48}
              allowSorting={false}
              allowFiltering={false}
              allowHiding={false}
              cellRender={({ data }: { data: BugListItem }) => (
                <SiteSupportAdminRowActions
                  viewHint="View bug report"
                  onView={() => void openView(data.bugId)}
                  onEdit={canManage ? () => void openEdit(data.bugId) : undefined}
                  editHint="Edit bug"
                  onDelete={canManage ? () => void deleteBug(data.bugId) : undefined}
                  deleteHint="Delete bug"
                />
              )}
            />
          </DataGrid>
        </div>
      </ScrollView>

      <GridDetailPopup
        visible={viewOpen}
        onClose={() => setViewOpen(false)}
        title="Bug Report"
        width={720}
        maxBodyHeight={720}
        sizeStorageKey={opsGridDetailPopupSizeKey(BUGS_GRID_STATE_KEY)}
        primaryActions={
          detail && canManage
            ? [
                {
                  text: detail.assigneeUserId ? 'Reclaim' : 'Claim',
                  onClick: () => void claimBug()
                },
                {
                  text: 'Edit',
                  type: 'default',
                  stylingMode: 'contained',
                  onClick: () => {
                    setViewOpen(false);
                    void openEdit(detail.bugId);
                  }
                },
                {
                  text: 'Delete',
                  type: 'danger',
                  onClick: () => void deleteBug(detail.bugId)
                }
              ]
            : undefined
        }
      >
        {renderViewContent()}
      </GridDetailPopup>

      <Popup
        visible={editOpen}
        onHiding={() => setEditOpen(false)}
        showTitle
        title="Edit Bug"
        width={480}
        height="auto"
        maxHeight="90vh"
        hideOnOutsideClick
        wrapperAttr={{ class: 'site-support-bug-popup' }}
        contentRender={renderEditContent}
      />

      <Popup
        visible={lightbox != null}
        onHiding={() => setLightbox(null)}
        showTitle
        title={lightbox?.fileName ?? 'Attachment'}
        width="80vw"
        height="80vh"
        maxWidth="80vw"
        maxHeight="80vh"
        hideOnOutsideClick
        dragEnabled={false}
        wrapperAttr={{ class: 'site-support-image-lightbox-popup' }}
        contentRender={() =>
          lightbox ? (
            <div className="site-support-image-lightbox">
              <img
                className="site-support-image-lightbox__img"
                src={api.attachmentUrl(lightbox.attachmentId, { inline: true })}
                alt={lightbox.fileName}
              />
              <a
                className="site-support-image-lightbox__download"
                href={api.attachmentUrl(lightbox.attachmentId, { download: true })}
                download={lightbox.fileName}
              >
                Download
              </a>
            </div>
          ) : null
        }
      />
    </>
  );
}
